import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLambdaFunctionContext } from '@context/LambdaFunctionContext';
import { ObjectId } from 'bson';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGeneralContext } from '@context/GeneralContext';
import URLS from '@/URLS';
import moment from 'moment';
import { useStoreSettings } from '@state/useStoreSettings';
import { useStoreOutlet } from '@state/useStoreOutlet';
import { useStoreViewBill } from '@state/useStoreViewBill';
import { useStoreSiccMember } from '@state/useStoreSiccMember';
import { getTableNames } from '@libs/Checkout/getTableNames';
import ReBillLayout from './viewbill/ReBillLayout';
import { useTokenCredentials } from '@state/useTokenCredentials';
import useRxdbMiniappReq from '@hooks/useRxdbMiniappReq';
import { useMiniappReq } from '@state/useMiniappReq';

const ViewBillPage = () => {
  const history = useHistory();
  const { getOutletInfo } = useStoreOutlet((state) => ({
    getOutletInfo: state.getOutletInfo,
  }));
  const { getSetting, getOrderMethod } = useStoreSettings((state) => ({
    getSetting: state.getSetting,
    getOrderMethod: state.getOrderMethod,
  }));
  const {
    getTransactionsId,
    getLastTransaction,
    getDetailTransaction,
  } = useLambdaFunctionContext();

  const { viewBillId: transId } = useStoreViewBill((state) => ({
    viewBillId: state.viewBillId,
  }));

  const { siccMember } = useStoreSiccMember((state) => ({
    siccMember: state.siccMember,
  }));

  const [showViewBill, setShowViewBillPage] = useState(true);
  const { showLoading } = useGeneralContext();
  const { getTokenCredentials } = useTokenCredentials((state) => ({
    getTokenCredentials: state.getTokenCredentials,
  }));
  const [ , subscribeViewBill ] = useRxdbMiniappReq();
  const { getMiniappReq, updateMiniappReqId } = useMiniappReq((state) => ({
    getMiniappReq: state.getMiniappReq,
    updateMiniappReqId: state.updateMiniappReqId
  }));
 
  const { t } = useTranslation();
  const [localState, setLocalState] = useState({
    outlet: '',
    table: '',
    billData: {},
    billTaxes: [],
    billDiscount: [],
    billSurcharge: [],
    billFooterNotes: [],
    billSubTotal: 0,
    billGrandTotal: 0,
    disabledRefresh: false,
    isShowHistory: false,
    loadingUpdate: false,
    isAddress: '',
    isSalesNo: '',
    rcptNo: '',
  });
  const mountedRef = useRef(true);
  const [showWarningMessage, setShowWarningMessage] = useState(false);

  const updateLocalState = useCallback((newData) => {
    setLocalState((prev) => ({
      ...prev,
      ...newData,
    }));
  }, []);

  const getLocalState = (key) => {
    return key ? localState[key] : localState;
  };

  const calculateSumTaxes = (oldTaxes, newTaxes) => {
    let sumTaxes = [];

    // Add null checks for parameters
    if (!Array.isArray(oldTaxes)) {
      oldTaxes = [];
    }
    if (!Array.isArray(newTaxes)) {
      return oldTaxes;
    }

    newTaxes.map((tax) => {
      let oldIndex = oldTaxes.findIndex((x) => x.TTax === tax.TTax);
      if (oldIndex !== -1) {
        oldTaxes[oldIndex].value = oldTaxes[oldIndex].value + tax.value;
        sumTaxes.push(oldTaxes[oldIndex]);
      } else {
        sumTaxes.push(tax);
      }
      return null;
    });

    return sumTaxes;
  };

  const standardizeData = (paramData) => {
    let dataBySalesType = {};
    let sumTaxes = [];
    let sumDiscount = [];
    let sumSurcharge = [];
    let sumSubTotal = 0;
    let sumGrandTotal = 0;

    let {
      transaction_items,
      sub_total,
      grand_total,
      taxes,
      discounts,
      surcharge,
      footer_notes,
    } = paramData;

    Array.isArray(transaction_items) &&
      transaction_items.map((item) => {
        if (
          typeof dataBySalesType[item.sales_type] === 'object' &&
          Object.keys(dataBySalesType[item.sales_type]).length > 0
        ) {
          // UPDATE OLD BILL DATA
          dataBySalesType[item.sales_type].transaction_items.push(item);
        } else {
          // NEW BILL DATA FOR THIS TYPE SALES
          let insertData = {
            transaction_items: [item],
          };

          dataBySalesType[item.sales_type] = { ...insertData };
        }
        return null;
      });

    sumTaxes = calculateSumTaxes(sumTaxes, taxes);
    sumDiscount = discounts;
    sumSurcharge = surcharge !== undefined ? surcharge : sumSurcharge;
    sumSubTotal = sumSubTotal + sub_total || 0;
    sumGrandTotal = sumGrandTotal + grand_total || 0;

    return {
      data: dataBySalesType,
      taxes: sumTaxes,
      discounts: sumDiscount,
      surcharge: sumSurcharge,
      // e.g. "* Not subject to GST" when the bill carries the PLU Surcharge
      footer_notes: Array.isArray(footer_notes) ? footer_notes : [],
      sub_total: sumSubTotal,
      grand_total: sumGrandTotal,
    };
  };

  /**
   * Processes transaction data and updates local bill state.
   * @param {Array} queryTrans - The list of transactions.
   * @param {Object} outletInfo - Outlet information containing outletName.
   */
  const getViewBillDetail = async (queryTrans, outletInfo) => {
    if (typeof queryTrans !== 'object') {
      updateLocalState({ billData: {}, isShowHistory: false });
      return;
    }

    // const isSplit = queryTrans.some((data) => data.is_split);
    if (queryTrans.is_split) {
      setShowWarningMessage(true);
    }
    // const result = queryTrans.filter((data) => !data.is_split);

    if (typeof queryTrans !== 'object') {
      updateLocalState({ billData: {}, isShowHistory: false });
      return;
    }
    const standardData = standardizeData(queryTrans);

    updateLocalState({
      outlet: outletInfo.outletName,
      table: queryTrans?.table_name,
      billData: { ...standardData.data },
      billTaxes: [...standardData.taxes],
      billDiscount: standardData.discounts ? [...standardData.discounts] : [],
      billSurcharge: [...standardData.surcharge],
          billFooterNotes: [...standardData.footer_notes],
      billSubTotal: standardData.sub_total,
      billGrandTotal: standardData.grand_total,
      isShowHistory: false,
      isAddress: queryTrans?.store_address,
      isSalesNo: queryTrans?.sales_no,
      rcptNo: queryTrans?.RcptNo,
    });
  };

  const prepareBillData = useCallback(async () => {
    if (!mountedRef.current) return;

    showLoading(true);
    setShowViewBillPage(false);
    // 1. cek tipe viewbill
    const orderMethod = getOrderMethod();
    const outletInfo = getOutletInfo();

    // 2. query berdasarkan type
    let filter = {};
    if (orderMethod === 'normal_order') {
      // normal order msh menggunakan table_id
      // sicc + normal order menggunakan parameter
      // cek jika tableName ada menggunakan table name + siccMember jika tdk ada pakai TA-sicc member
      filter = {
        table_id: outletInfo.tableId,
        parameter: getSetting().tableName
          ? getTableNames(siccMember, getSetting(), getSetting().tableName)
          : siccMember && typeof siccMember === 'string'
            ? `TA-${siccMember.substring(0, siccMember.length - 2)}`
            : '',
        type_bill: orderMethod,
      };
    }

    if (orderMethod === 'waiter_order') {
      let transactionsId = getTransactionsId();
      if (Array.isArray(transactionsId) && transactionsId.length > 0) {
        let lastTransId = transactionsId[transactionsId.length - 1];
        filter = {
          table_id: outletInfo.tableId,
          status: { $nin: ['expired'] }, // status langsung dari realm
          type_bill: orderMethod,
          _id: new ObjectId(lastTransId),
        };
      }
    }

    let queryCheck = null;
    if (['normal_order', 'waiter_order'].indexOf(orderMethod) > -1) {
      if (getSetting().customTable !== 'off') {
        filter['_id'] = new ObjectId(transId);
        delete filter['table_id'];
      }
      // Jika sicc member true hapus _id (transId),
      // jika false hapus parameter,
      // Parameter hanya di gunakan untuk sicc
      if (getSetting().useSiccMember) {
        delete filter['_id'];
        if (!filter['table_id']) delete filter['table_id'];
      } else {
        delete filter['parameter'];
      }
      queryCheck = await getLastTransaction(filter);
    }

    const substract = moment().subtract(3, 'h');
    filter['createdAt'] = { $gt: substract.toDate() };
    if (queryCheck && queryCheck.is_history_exists) {
      if (queryCheck.calculated_id) {
        let queryTrans = null;
        filter['status'] = 'calculated';
        if (['normal_order', 'waiter_order'].indexOf(orderMethod) > -1) {
          filter['_id'] = new ObjectId(queryCheck.calculated_id);
          //jika pertama kali buka view bill dalam mode normal order pake fungsi ini
          const id = new ObjectId().toString();
          updateMiniappReqId(id);
          filter['miniapp_request_rxdb_id'] = id;
          queryTrans = await getDetailTransaction(filter);
          if (queryTrans && mountedRef.current) {
            getViewBillDetail(queryTrans, outletInfo);
          }
          subscribeViewBill();
        } else {
          if (queryTrans && mountedRef.current) {
            getViewBillDetail(queryTrans, outletInfo);
          }
        }

        if (!mountedRef.current) return;

        showLoading(false);
        setShowViewBillPage(true);
      } else {
        if (mountedRef.current) {
          updateLocalState({ billData: {} });
          updateLocalState({
            isShowHistory:
              getSetting().useSiccMember && !outletInfo.tableId ? false : true,
          });
        }
      }
    } else {
      if (mountedRef.current) {
        updateLocalState({ billData: {} });
        updateLocalState({ isShowHistory: false });
      }
    }

    if (mountedRef.current) {
      showLoading(false);
      setShowViewBillPage(true);
    }
  }, []);

  const getNameSalesType = (paramId) => {
    if (paramId) {
      let outletInfo = getOutletInfo();
      let salesTypes = outletInfo.salestype.sales;

      let index = salesTypes.findIndex(
        (x) => x.type_sales_id.toString() === paramId.toString(),
      );

      return salesTypes[index]?.title;
    }
    return null;
  };

  useEffect(() => {
    const userAgent = navigator.userAgent;
    if (userAgent.match(/safari/i)) {
      window.history.replaceState(null, null, history.location.pathname);
    }
    1;
    // MATIKAN TOMBOL BACK
    window.history.pushState(null, null, history.location.pathname);
    window.addEventListener('popstate', () => {
      history.go(1);
    });

    // watchTransaction();
    prepareBillData();

    return function () {
      mountedRef.current = false;
    };
    // eslint-disable-next-line
  }, [getTokenCredentials(), getMiniappReq() === 'updated']);

  const handleClickRefresh = () => {
    showLoading(true);
    prepareBillData();
    updateLocalState({ disabledRefresh: true });
    setTimeout(() => {
      updateLocalState({ disabledRefresh: false });
    }, 100);
  };

  return (
    <ReBillLayout
      onBack={URLS.MENU_CATALOGUE}
      title={
        getLocalState().billData &&
          Object.keys(getLocalState().billData).length !== 0
          ? 'VIEW BILL'
          : t('viewBillTitle')?.toUpperCase()
      }
      showViewBill={showViewBill}
      billData={getLocalState().billData}
      table={getLocalState().table}
      salesType={(param) => getNameSalesType(param)}
      outlet={getLocalState().outlet}
      disableRefresh={getLocalState().disabledRefresh}
      onClickRefresh={() => handleClickRefresh()}
      billDiscount={getLocalState().billDiscount}
      billSurcharge={getLocalState().billSurcharge}
      billFooterNotes={getLocalState().billFooterNotes}
      billSubTotal={getLocalState().billSubTotal}
      billGrandTotal={getLocalState().billGrandTotal}
      billTaxes={getLocalState().billTaxes}
      warningMessage={showWarningMessage}
      showHistory={getLocalState().isShowHistory}
      loadingUpdate={getLocalState().loadingUpdate}
      addressStore={getLocalState().isAddress}
      salesNo={getLocalState().isSalesNo}
      rcptNo={getLocalState().rcptNo}
    />
  );
};

export default ViewBillPage;
